import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message
} from 'antd';
import {
  CalendarOutlined,
  EditOutlined,
  PlusOutlined,
  QrcodeOutlined,
  StopOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import moment from 'moment';
import api from '../../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const settlementModeOptions = [
  { value: 'global_points', label: '同步学校积分' },
  { value: 'manual_publish', label: '活动内记分，后续统一发布' },
  { value: 'event_only', label: '仅活动内排行' }
];

const eventStatusOptions = [
  { value: 'draft', label: '草稿' },
  { value: 'active', label: '进行中' },
  { value: 'completed', label: '已结束' },
  { value: 'archived', label: '已归档' }
];

const projectStatusOptions = [
  { value: 'draft', label: '草稿' },
  { value: 'active', label: '启用' },
  { value: 'disabled', label: '停用' }
];

function renderStatusTag(status) {
  const map = {
    draft: <Tag>草稿</Tag>,
    active: <Tag color="processing">进行中</Tag>,
    completed: <Tag color="success">已结束</Tag>,
    archived: <Tag color="default">已归档</Tag>,
    disabled: <Tag color="error">停用</Tag>
  };
  return map[status] || <Tag>{status}</Tag>;
}

function renderSettlementTag(mode) {
  const map = {
    global_points: <Tag color="gold">同步积分</Tag>,
    manual_publish: <Tag color="purple">手动发布</Tag>,
    event_only: <Tag color="blue">仅活动榜</Tag>
  };
  return map[mode] || <Tag>{mode}</Tag>;
}

function toRangeValue(record) {
  if (!record?.start_at && !record?.end_at) {
    return undefined;
  }
  return [
    record?.start_at ? moment(record.start_at) : null,
    record?.end_at ? moment(record.end_at) : null
  ];
}

const ActivityScanManagementV2 = () => {
  const [events, setEvents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projectLoading, setProjectLoading] = useState(false);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  const [savingProject, setSavingProject] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [currentProject, setCurrentProject] = useState(null);
  const [eventDrawerVisible, setEventDrawerVisible] = useState(false);
  const [projectDrawerVisible, setProjectDrawerVisible] = useState(false);
  const [projectEditorVisible, setProjectEditorVisible] = useState(false);
  const [leaderboardVisible, setLeaderboardVisible] = useState(false);
  const [eventMode, setEventMode] = useState('create');
  const [projectMode, setProjectMode] = useState('create');
  const [eventForm] = Form.useForm();
  const [projectForm] = Form.useForm();

  useEffect(() => {
    fetchBaseData();
  }, []);

  const teacherOptions = useMemo(
    () =>
      teachers.map((teacher) => ({
        value: teacher.id,
        label: `${teacher.name}${teacher.subject ? ` / ${teacher.subject}` : ''}`
      })),
    [teachers]
  );

  const fetchBaseData = async () => {
    setLoading(true);
    try {
      const [eventsResponse, teachersResponse] = await Promise.all([
        api.get('/activity-scan/events'),
        api.get('/teachers')
      ]);

      if (eventsResponse.data.success) {
        setEvents(eventsResponse.data.data || []);
      } else {
        message.error(eventsResponse.data.message || '获取活动列表失败');
      }

      if (teachersResponse.data.success) {
        setTeachers(teachersResponse.data.data || []);
      } else {
        message.error(teachersResponse.data.message || '获取教师列表失败');
      }
    } catch (error) {
      console.error('获取活动基础数据失败:', error);
      message.error(error.response?.data?.message || '获取活动基础数据失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async (eventId) => {
    setProjectLoading(true);
    try {
      const response = await api.get(`/activity-scan/events/${eventId}/projects`);
      if (response.data.success) {
        setProjects(response.data.data || []);
      } else {
        message.error(response.data.message || '获取活动项目失败');
      }
    } catch (error) {
      console.error('获取活动项目失败:', error);
      message.error(error.response?.data?.message || '获取活动项目失败');
    } finally {
      setProjectLoading(false);
    }
  };

  const fetchLeaderboard = async (eventId) => {
    setLeaderboardLoading(true);
    try {
      const response = await api.get(`/activity-scan/events/${eventId}/leaderboard`);
      if (response.data.success) {
        setLeaderboard(response.data.data?.leaderboard || []);
      } else {
        message.error(response.data.message || '获取活动排行榜失败');
      }
    } catch (error) {
      console.error('获取活动排行榜失败:', error);
      message.error(error.response?.data?.message || '获取活动排行榜失败');
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const refreshCurrentProjects = async () => {
    if (projectDrawerVisible && currentEvent?.id) {
      await fetchProjects(currentEvent.id);
    }
  };

  const openCreateEventDrawer = () => {
    setEventMode('create');
    setCurrentEvent(null);
    eventForm.resetFields();
    eventForm.setFieldsValue({
      activityType: 'general',
      settlementMode: 'global_points',
      status: 'active'
    });
    setEventDrawerVisible(true);
  };

  const openEditEventDrawer = (record) => {
    setEventMode('edit');
    setCurrentEvent(record);
    eventForm.setFieldsValue({
      title: record.title,
      description: record.description,
      activityType: record.activity_type,
      settlementMode: record.settlement_mode,
      status: record.status,
      timeRange: toRangeValue(record)
    });
    setEventDrawerVisible(true);
  };

  const saveEvent = async () => {
    try {
      const values = await eventForm.validateFields();
      const payload = {
        title: values.title,
        description: values.description,
        activityType: values.activityType,
        settlementMode: values.settlementMode,
        status: values.status,
        startAt: values.timeRange?.[0] ? values.timeRange[0].format('YYYY-MM-DD HH:mm:ss') : null,
        endAt: values.timeRange?.[1] ? values.timeRange[1].format('YYYY-MM-DD HH:mm:ss') : null
      };

      setSavingEvent(true);
      const response = eventMode === 'edit' && currentEvent
        ? await api.put(`/activity-scan/events/${currentEvent.id}`, payload)
        : await api.post('/activity-scan/events', payload);

      if (!response.data.success) {
        message.error(response.data.message || '保存活动失败');
        return;
      }

      message.success(eventMode === 'edit' ? '活动更新成功' : '活动创建成功');
      setEventDrawerVisible(false);
      eventForm.resetFields();
      await fetchBaseData();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      console.error('保存活动失败:', error);
      message.error(error.response?.data?.message || '保存活动失败');
    } finally {
      setSavingEvent(false);
    }
  };

  const changeEventStatus = async (record, status) => {
    try {
      const response = await api.put(`/activity-scan/events/${record.id}`, {
        title: record.title,
        description: record.description,
        activityType: record.activity_type,
        settlementMode: record.settlement_mode,
        status,
        startAt: record.start_at,
        endAt: record.end_at
      });
      if (!response.data.success) {
        message.error(response.data.message || '更新活动状态失败');
        return;
      }
      message.success(status === 'completed' ? '活动已结束' : '活动已重新开启');
      await fetchBaseData();
    } catch (error) {
      console.error('更新活动状态失败:', error);
      message.error(error.response?.data?.message || '更新活动状态失败');
    }
  };

  const openProjectsDrawer = async (event) => {
    setCurrentEvent(event);
    setProjectDrawerVisible(true);
    await fetchProjects(event.id);
  };

  const openCreateProjectDrawer = (event) => {
    setProjectMode('create');
    setCurrentEvent(event);
    setCurrentProject(null);
    projectForm.resetFields();
    projectForm.setFieldsValue({
      judgeTeacherIds: [],
      maxScore: 100,
      pointsRatio: 1,
      status: 'active'
    });
    setProjectEditorVisible(true);
  };

  const openEditProjectDrawer = (event, project) => {
    setProjectMode('edit');
    setCurrentEvent(event);
    setCurrentProject(project);
    projectForm.setFieldsValue({
      title: project.title,
      description: project.description,
      judgeTeacherIds: project.judge_teacher_ids || [],
      maxScore: Number(project.max_score || 100),
      pointsRatio: Number(project.points_ratio || 1),
      status: project.status
    });
    setProjectEditorVisible(true);
  };

  const saveProject = async () => {
    if (!currentEvent) {
      return;
    }

    try {
      const values = await projectForm.validateFields();
      const payload = {
        title: values.title,
        description: values.description,
        judgeTeacherIds: values.judgeTeacherIds,
        maxScore: values.maxScore,
        pointsRatio: values.pointsRatio,
        status: values.status
      };

      setSavingProject(true);
      const response = projectMode === 'edit' && currentProject
        ? await api.put(`/activity-scan/projects/${currentProject.id}`, payload)
        : await api.post(`/activity-scan/events/${currentEvent.id}/projects`, payload);

      if (!response.data.success) {
        message.error(response.data.message || '保存项目失败');
        return;
      }

      message.success(projectMode === 'edit' ? '项目更新成功' : '项目创建成功');
      setProjectEditorVisible(false);
      projectForm.resetFields();
      await fetchBaseData();
      await refreshCurrentProjects();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      console.error('保存项目失败:', error);
      message.error(error.response?.data?.message || '保存项目失败');
    } finally {
      setSavingProject(false);
    }
  };

  const changeProjectStatus = async (project, status) => {
    try {
      const response = await api.put(`/activity-scan/projects/${project.id}`, {
        title: project.title,
        description: project.description,
        judgeTeacherIds: project.judge_teacher_ids || [],
        maxScore: project.max_score,
        pointsRatio: project.points_ratio,
        status
      });
      if (!response.data.success) {
        message.error(response.data.message || '更新项目状态失败');
        return;
      }
      message.success(status === 'disabled' ? '项目已停用' : '项目已启用');
      await fetchBaseData();
      await refreshCurrentProjects();
    } catch (error) {
      console.error('更新项目状态失败:', error);
      message.error(error.response?.data?.message || '更新项目状态失败');
    }
  };

  const openLeaderboardDrawer = async (event) => {
    setCurrentEvent(event);
    setLeaderboardVisible(true);
    await fetchLeaderboard(event.id);
  };

  const eventColumns = useMemo(
    () => [
      {
        title: '活动',
        dataIndex: 'title',
        key: 'title',
        render: (_, record) => (
          <Space direction="vertical" size={4}>
            <Space wrap>
              <Text strong>{record.title}</Text>
              {renderStatusTag(record.status)}
              {renderSettlementTag(record.settlement_mode)}
            </Space>
            <Text type="secondary">
              {record.activity_type || 'general'}
              {record.start_at ? ` · ${moment(record.start_at).format('YYYY-MM-DD HH:mm')}` : ''}
            </Text>
            {record.description ? <Text type="secondary">{record.description}</Text> : null}
          </Space>
        )
      },
      {
        title: '项目数',
        dataIndex: 'project_count',
        width: 90
      },
      {
        title: '评分流水',
        dataIndex: 'score_count',
        width: 100
      },
      {
        title: '活动总分',
        dataIndex: 'total_score',
        width: 120,
        render: (value) => Number(value || 0)
      },
      {
        title: '操作',
        key: 'action',
        width: 430,
        render: (_, record) => (
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreateProjectDrawer(record)}>
              新建项目
            </Button>
            <Button icon={<QrcodeOutlined />} onClick={() => openProjectsDrawer(record)}>
              查看项目
            </Button>
            <Button icon={<TrophyOutlined />} onClick={() => openLeaderboardDrawer(record)}>
              排行榜
            </Button>
            <Button icon={<EditOutlined />} onClick={() => openEditEventDrawer(record)}>
              编辑活动
            </Button>
            {record.status === 'active' ? (
              <Button icon={<StopOutlined />} onClick={() => changeEventStatus(record, 'completed')}>
                结束活动
              </Button>
            ) : (
              <Button onClick={() => changeEventStatus(record, 'active')}>
                重新开启
              </Button>
            )}
          </Space>
        )
      }
    ],
    []
  );

  const projectColumns = useMemo(
    () => [
      {
        title: '项目',
        dataIndex: 'title',
        key: 'title',
        render: (_, record) => (
          <Space direction="vertical" size={4}>
            <Space wrap>
              <Text strong>{record.title}</Text>
              {renderStatusTag(record.status)}
            </Space>
            <Space wrap size={[0, 8]}>
              {(record.judge_teacher_names || []).map((name) => (
                <Tag color="blue" key={`${record.id}-${name}`}>
                  {name}
                </Tag>
              ))}
            </Space>
            <Text type="secondary">
              满分 {Number(record.max_score || 0)} · 积分系数 {Number(record.points_ratio || 0)} · {Number(record.judge_count || 0)} 位评委
            </Text>
            {record.description ? <Text type="secondary">{record.description}</Text> : null}
          </Space>
        )
      },
      {
        title: '评分次数',
        dataIndex: 'score_count',
        width: 110
      },
      {
        title: '已出结果',
        dataIndex: 'scored_student_count',
        width: 110
      },
      {
        title: '累计均分',
        dataIndex: 'total_score',
        width: 120,
        render: (value) => Number(value || 0)
      },
      {
        title: '操作',
        key: 'action',
        width: 220,
        render: (_, record) => (
          <Space wrap>
            <Button icon={<EditOutlined />} onClick={() => openEditProjectDrawer(currentEvent, record)}>
              编辑
            </Button>
            {record.status === 'disabled' ? (
              <Button onClick={() => changeProjectStatus(record, 'active')}>
                启用
              </Button>
            ) : (
              <Button icon={<StopOutlined />} onClick={() => changeProjectStatus(record, 'disabled')}>
                停用
              </Button>
            )}
          </Space>
        )
      }
    ],
    [currentEvent]
  );

  const leaderboardColumns = useMemo(
    () => [
      { title: '排名', dataIndex: 'rank', width: 80 },
      {
        title: '学生',
        dataIndex: 'student_name',
        render: (_, record) => (
          <Space direction="vertical" size={2}>
            <Text strong>{record.student_name}</Text>
            <Text type="secondary">{record.class_name || `班级 ${record.class_id}`}</Text>
          </Space>
        )
      },
      { title: '参与项目', dataIndex: 'project_count', width: 110 },
      {
        title: '总分',
        dataIndex: 'total_score',
        width: 110,
        render: (value) => Number(value || 0)
      },
      {
        title: '积分',
        dataIndex: 'total_points',
        width: 110,
        render: (value) => Number(value || 0)
      }
    ],
    []
  );

  return (
    <Card bordered={false}>
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <div>
          <Title level={4} style={{ marginBottom: 8 }}>活动扫码管理</Title>
          <Text type="secondary">
            由学校管理员先创建活动和项目，配置评分教师与结算方式，再由教师在微信小程序“教师工具”里扫码学生通用码进行评分。
          </Text>
        </div>

        <Card size="small" style={{ background: '#fafcff', borderColor: '#dbe7ff' }}>
          <Space direction="vertical" size={8}>
            <Text strong>当前版本已支持</Text>
            <Text type="secondary">1. 学校管理员创建活动、编辑活动状态、结束活动</Text>
            <Text type="secondary">2. 一个项目配置多位评委，系统按平均分生成项目结果</Text>
            <Text type="secondary">3. 教师扫码评分后自动汇总活动排行榜</Text>
            <Text type="secondary">4. 活动可选择同步学校积分、手动发布积分或仅做活动内排行</Text>
          </Space>
        </Card>

        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateEventDrawer}>
            新建活动
          </Button>
          <Button onClick={fetchBaseData}>刷新</Button>
        </Space>

        <Table rowKey="id" loading={loading} columns={eventColumns} dataSource={events} pagination={{ pageSize: 10 }} />
      </Space>

      <Drawer
        title={eventMode === 'edit' ? '编辑学校活动' : '新建学校活动'}
        open={eventDrawerVisible}
        onClose={() => setEventDrawerVisible(false)}
        width={520}
        extra={
          <Space>
            <Button onClick={() => setEventDrawerVisible(false)}>取消</Button>
            <Button type="primary" loading={savingEvent} onClick={saveEvent}>
              {eventMode === 'edit' ? '保存活动' : '创建活动'}
            </Button>
          </Space>
        }
      >
        <Form form={eventForm} layout="vertical">
          <Form.Item label="活动名称" name="title" rules={[{ required: true, message: '请输入活动名称' }]}>
            <Input placeholder="例如：2026 艺术作品展评" />
          </Form.Item>
          <Form.Item label="活动说明" name="description">
            <TextArea rows={4} placeholder="说明活动范围、评分规则、参与对象和组织方式" />
          </Form.Item>
          <Form.Item label="活动类型" name="activityType">
            <Input placeholder="例如：math_festival / art_show / speech_contest" />
          </Form.Item>
          <Form.Item label="积分结算方式" name="settlementMode">
            <Select options={settlementModeOptions} />
          </Form.Item>
          <Form.Item label="活动状态" name="status">
            <Select options={eventStatusOptions} />
          </Form.Item>
          <Form.Item label="活动时间" name="timeRange">
            <RangePicker showTime style={{ width: '100%' }} suffixIcon={<CalendarOutlined />} />
          </Form.Item>
        </Form>
      </Drawer>

      <Drawer
        title={
          projectMode === 'edit'
            ? `编辑项目${currentEvent ? ` · ${currentEvent.title}` : ''}`
            : `新建项目${currentEvent ? ` · ${currentEvent.title}` : ''}`
        }
        open={projectEditorVisible}
        onClose={() => setProjectEditorVisible(false)}
        width={560}
        extra={
          <Space>
            <Button onClick={() => setProjectEditorVisible(false)}>取消</Button>
            <Button type="primary" loading={savingProject} onClick={saveProject}>
              {projectMode === 'edit' ? '保存项目' : '创建项目'}
            </Button>
          </Space>
        }
      >
        <Form form={projectForm} layout="vertical">
          <Form.Item label="项目名称" name="title" rules={[{ required: true, message: '请输入项目名称' }]}>
            <Input placeholder="例如：24 点挑战 / 美术作品评分 / 英语朗读展示" />
          </Form.Item>
          <Form.Item label="项目说明" name="description">
            <TextArea rows={4} placeholder="说明评分标准、作品要求、活动内容等" />
          </Form.Item>
          <Form.Item
            label="评分教师"
            name="judgeTeacherIds"
            rules={[{ required: true, message: '请选择至少一位评分教师' }]}
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              options={teacherOptions}
              placeholder="可选择多位教师作为评委"
            />
          </Form.Item>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item label="项目满分" name="maxScore" rules={[{ required: true, message: '请输入项目满分' }]}>
              <InputNumber min={1} max={1000} precision={2} style={{ width: 180 }} />
            </Form.Item>
            <Form.Item label="积分系数" name="pointsRatio" rules={[{ required: true, message: '请输入积分系数' }]}>
              <InputNumber min={0} max={100} step={0.1} precision={1} style={{ width: 180 }} />
            </Form.Item>
          </Space>
          <Form.Item label="项目状态" name="status">
            <Select options={projectStatusOptions} />
          </Form.Item>
        </Form>
      </Drawer>

      <Drawer
        title={currentEvent ? `${currentEvent.title} · 项目列表` : '项目列表'}
        open={projectDrawerVisible}
        onClose={() => setProjectDrawerVisible(false)}
        width={980}
        extra={
          currentEvent ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreateProjectDrawer(currentEvent)}>
              新建项目
            </Button>
          ) : null
        }
      >
        <Table rowKey="id" loading={projectLoading} columns={projectColumns} dataSource={projects} pagination={false} />
      </Drawer>

      <Drawer
        title={currentEvent ? `${currentEvent.title} · 活动排行榜` : '活动排行榜'}
        open={leaderboardVisible}
        onClose={() => setLeaderboardVisible(false)}
        width={760}
      >
        <Table
          rowKey="student_id"
          loading={leaderboardLoading}
          columns={leaderboardColumns}
          dataSource={leaderboard}
          pagination={{ pageSize: 20 }}
        />
      </Drawer>
    </Card>
  );
};

export default ActivityScanManagementV2;
